import apiClient from './client'

export interface AuditLog {
  id: string
  team_id: string
  user_id: string
  action: string
  resource_type: string
  resource_id: string | null
  resource_name: string | null
  changes: Record<string, { old: unknown; new: unknown }> | null
  ip_address: string | null
  created_at: string
}

export interface AuditLogListResponse {
  logs: AuditLog[]
  total: number
}

export interface AuditSummaryResponse {
  total_actions: number
  by_action: Record<string, number>
  active_users: number
  period_days: number
}

export interface AuditLogFilters {
  user_id?: string
  action?: string
  resource_type?: string
  resource_id?: string
  start_date?: string
  end_date?: string
  limit?: number
  offset?: number
}

export const auditApi = {
  /**
   * List audit logs with optional filters
   */
  list: async (filters: AuditLogFilters = {}): Promise<AuditLogListResponse> => {
    const params = new URLSearchParams()
    if (filters.user_id) params.append('user_id', filters.user_id)
    if (filters.action) params.append('action', filters.action)
    if (filters.resource_type) params.append('resource_type', filters.resource_type)
    if (filters.resource_id) params.append('resource_id', filters.resource_id)
    if (filters.start_date) params.append('start_date', filters.start_date)
    if (filters.end_date) params.append('end_date', filters.end_date)
    if (filters.limit) params.append('limit', filters.limit.toString())
    if (filters.offset) params.append('offset', filters.offset.toString())

    const response = await apiClient.get<AuditLogListResponse>('/audit', { params })
    return response.data
  },

  /**
   * Get recent activity
   */
  getRecent: async (days: number = 7, limit: number = 20): Promise<AuditLog[]> => {
    const response = await apiClient.get<AuditLog[]>('/audit/recent', {
      params: { days, limit },
    })
    return response.data
  },

  /**
   * Get activity summary
   */
  getSummary: async (days: number = 30): Promise<AuditSummaryResponse> => {
    const response = await apiClient.get<AuditSummaryResponse>('/audit/summary', {
      params: { days },
    })
    return response.data
  },

  /**
   * Get user activity
   */
  getUserActivity: async (userId: string, limit: number = 50): Promise<AuditLog[]> => {
    const response = await apiClient.get<AuditLog[]>(`/audit/users/${userId}`, {
      params: { limit },
    })
    return response.data
  },

  /**
   * Get resource history
   */
  getResourceHistory: async (
    resourceType: string,
    resourceId: string,
    limit: number = 50
  ): Promise<AuditLog[]> => {
    const response = await apiClient.get<AuditLog[]>(
      `/audit/resources/${resourceType}/${resourceId}`,
      { params: { limit } }
    )
    return response.data
  },
}
